/**
 * `use-evidence-sync` (W45-F5-S1, #1476) — the React surface over the offline
 * evidence queue + sync worker.
 *
 * Two hooks:
 *   - `useEvidenceSyncStatus` — subscribes to the worker's queue-status
 *     snapshots (pending count, syncing, failed, online) for the topbar
 *     indicator. Starts the worker (online listener) on first mount.
 *   - `useCaptureEvidence` — enqueue helper for the capture flow. Always
 *     enqueues first (durable), then kicks a drain — so a capture never fails
 *     just because the network blipped mid-upload.
 *
 * The worker's access-token getter is wired from `useAuth` here so the
 * framework-agnostic worker stays auth-library-free.
 */

import * as React from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { getEvidenceQueue, type EvidenceQueueItem } from './evidence-queue';
import {
  getEvidenceSyncWorker,
  type EvidenceSyncSnapshot,
} from './sync-worker';

const EMPTY_SNAPSHOT: EvidenceSyncSnapshot = {
  pending: 0,
  syncing: false,
  failed: 0,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
};

export interface EvidenceSyncStatus extends EvidenceSyncSnapshot {
  /** Items currently in the queue, for the pending-list popover. */
  items: EvidenceQueueItem[];
  /** Manual retry for one failed item. */
  retry: (id: string) => Promise<void>;
  /** Force a drain of the whole queue (manual "sync now"). */
  syncNow: () => Promise<void>;
}

export function useEvidenceSyncStatus(): EvidenceSyncStatus {
  const { getAccessToken } = useAuth();
  const worker = React.useMemo(
    () => getEvidenceSyncWorker({ getAccessToken }),
    [getAccessToken],
  );
  const queue = React.useMemo(() => getEvidenceQueue(), []);

  const [snapshot, setSnapshot] =
    React.useState<EvidenceSyncSnapshot>(EMPTY_SNAPSHOT);
  const [items, setItems] = React.useState<EvidenceQueueItem[]>([]);

  const refreshItems = React.useCallback(() => {
    void queue.list().then(setItems);
  }, [queue]);

  React.useEffect(() => {
    const detach = worker.start();
    const unsubscribe = worker.subscribe((next) => {
      setSnapshot(next);
      refreshItems();
    });
    refreshItems();
    return () => {
      unsubscribe();
      detach();
    };
  }, [worker, refreshItems]);

  const retry = React.useCallback(
    async (id: string) => {
      await worker.retry(id);
      refreshItems();
    },
    [worker, refreshItems],
  );

  const syncNow = React.useCallback(async () => {
    await worker.drain();
    refreshItems();
  }, [worker, refreshItems]);

  return { ...snapshot, items, retry, syncNow };
}

export interface CaptureEvidenceInput {
  blobs: Blob[];
  filenames: string[];
  note: string;
  buildingId: string;
  lat?: number;
  lng?: number;
}

/**
 * Always-enqueue-then-sync capture. Returns the persisted item so the UI can
 * show an optimistic "captured" state. A drain is kicked but not awaited — the
 * worker reports progress through the status snapshot.
 */
export function useCaptureEvidence(): (
  input: CaptureEvidenceInput,
) => Promise<EvidenceQueueItem> {
  const { getAccessToken } = useAuth();
  const queue = React.useMemo(() => getEvidenceQueue(), []);
  const worker = React.useMemo(
    () => getEvidenceSyncWorker({ getAccessToken }),
    [getAccessToken],
  );

  return React.useCallback(
    async (input: CaptureEvidenceInput) => {
      const item = await queue.enqueue(input);
      void worker.drain();
      return item;
    },
    [queue, worker],
  );
}
